import React, {useEffect, useReducer, useState} from 'react';
import {Context} from './Context';
import {initialState, reducer} from './Reducer';
import {
  IContext,
  ACTION_VERBS,
  IProfile as IProfileState,
  IUiConfiguration,
  IBank,
} from './Types';

import * as apiService from '../../services';

import {LoginState, ErrorCodes, Role} from '../../constants/enumerations';
import {
  IBalanceSheetResponse,
  IClientListResponse,
  IDebtorListResponse,
  IEntitiesResponse,
  IHistoryResponse,
  IReceivableListResponse,
} from '../../interfaces/services';
import {IListOption} from '../../interfaces/globals';
import {IBalanceListResponse} from '../../interfaces/services/balances.interface';
import {INotificationListResponse} from '../../interfaces/services/notification-list-response.interface';
import {ISignupForm} from '../../interfaces/ui';
import {
  ResultState,
  SignUpState,
} from '../../constants/enumerations/state.enum';
import {IProfile} from '../../interfaces/services/profile.interface';
import INotification from '../../interfaces/dtos/notification.interface';

import {IStandardServiceResponse} from '../../interfaces/services/standard-service.interface';
import {
  IBalanceSheetItem,
  IClient,
  IDebtorItem,
  IReceivable,
  ISemaphoreValue,
} from '../../interfaces/dtos';
import {ISemaphoreResponse} from '../../interfaces/services/semaphore-response.interface';
import {verifyIsOutdated} from '../../utils/versions/verifyIsOutdated';
import {
  handleError,
  handleLoginCredentialsFail,
} from '../../utils/exceptions/handle-error';
import {isWebApp} from '../../utils/platform/is-web';
import useSession from '../../hooks/session/useSession';
import {
  USER_STATE_KEY,
  clearFromStorage,
  loadFromStorage,
  saveToStorage,
} from '../../utils/storage/local-storage';
import {useNetInfo} from '../../utils/netinfo';

const DATA_SESSION_KEY = 'DATA-SESSION';

/**
 *
 * @param props
 * @returns
 */
export const UserProvider = (props: any) => {
  const {
    SIGN_IN,
    STATE_PROCESS_ERROR,
    SIGN_OUT,
    CLEAR_ERRORS,
    ENTITIES,
    BALANCE_LIST,
    NOTIFICATION_LIST,
    SIGN_UP,
    CHANGE_PASSWORD,
    RECOVER_PASSWORD,
    SET_AUTHENTICATED,
    SEND_EMAIL_BALANCE,
    GET_TYC,
    BALANCE_SHEET,
    REMOVE_ACCOUNT,
    HISTORY,
    SEMAPHORE_ITEMS,
    DEBTOR_LIST,
    RECEIVABLE_LIST,
    IS_OUTDATED,
    REGISTER_DEVICE,
    APP_INFO,
    MARK_NOTIFICATIONS_READ,
    ADD_NOTIFICATION,
    GO_TO_MESSAGES,
    GET_SALESMAN_CLIENTS,
    SET_SALESMAN_CLIENT,
    SET_LOCALIZATION_ACTIVE,
    LOAD_STATE_FROM_STORE,
    SET_REMOTE_MESSAGE_ERROR,
  } = ACTION_VERBS;

  const netInfo = useNetInfo();
  const [connected, setIsConnected] = useState(
    netInfo.isConnected as boolean | null,
  );

  /**
   *
   */
  const [state, dispatch] = useReducer(reducer, initialState);
  const [sessionValue, saveSessionValue, sessionValueUtils] =
    useSession(DATA_SESSION_KEY);

  const isConnected = (): boolean => {
    if (connected) {
      return connected;
    }
    return false;
  };

  useEffect(() => {
    setIsConnected(netInfo.isConnected);
  }, [netInfo]);

  /**
   *
   * @param group
   * @param payload
   */
  const getPayloadCredentials = (payload: any) => {
    if (payload.success && payload.credentials) {
      const {token, begin, lifetime, reset} = payload.credentials;
      if (token && begin && lifetime) {
        return {
          token,
          begin,
          lifetime,
          reset,
        };
      }
    }
    return null;
  };

  /**
   *
   * @param payload
   * @returns
   */
  const getPayloadFaq = (payload: any): string => {
    if (payload.success) {
      const {faq} = payload;
      if (faq) {
        return faq;
      }
    }
    return '';
  };

  /**
   *
   * @param payload
   * @returns
   */
  const getPayloadTyc = (payload: any): string | null => {
    if (payload) {
      const {tyc} = payload;
      if (tyc) {
        return tyc;
      }
    }
    return null;
  };

  /**
   *
   * @param payload
   * @returns
   */
  const getPayloadPhone = (payload: any): string => {
    if (payload.success) {
      const {phone} = payload;
      if (phone) {
        return phone;
      }
    }
    return '';
  };

  /**
   *
   * @param payload
   * @returns
   */
  const getPayloadWhatsApp = (payload: any): string => {
    if (payload.success) {
      const {whatsapp} = payload;

      if (whatsapp) {
        return whatsapp;
      }
    }
    return '';
  };

  /**
   *
   * @param payload
   * @returns
   */
  const getPayloadInfo = (payload: any) => {
    if (payload.success) {
      const {email} = payload;
      if (email) {
        return {
          email,
        };
      }
    }
    return null;
  };

  const getEntities = (response: IEntitiesResponse): IListOption[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getSalesmanTerritoriesAtLogin = (payload: any): IListOption[] => {
    if (payload.salesman && payload.salesman.salesTerritories) {
      return payload.salesman.salesTerritories as IListOption[];
    }
    return [];
  };

  const getSalesmanCustomerTypesAtLogin = (payload: any): IListOption[] => {
    if (payload.salesman && payload.salesman.customerTypes) {
      return payload.salesman.customerTypes as IListOption[];
    }
    return [];
  };

  const getRequiredCustomerDocumentList = (payload: any): IListOption[] => {
    if (payload.salesman && payload.salesman.requiredDocumentList) {
      return payload.salesman.requiredDocumentList as IListOption[];
    }
    return [];
  };

  const getCustomerPaymentOptions = (payload: any): IListOption[] => {
    if (payload.salesman && payload.salesman.paymentOptions) {
      return payload.salesman.paymentOptions as IListOption[];
    }
    return [];
  };

  const getEntitiesAtLogin = (payload: any): IListOption[] => {
    if (payload.entities) {
      return payload.entities as IListOption[];
    }
    return [];
  };

  const getBanks = (payload: {banks: IBank[]}) => {
    return payload.banks.map(bank => {
      const {title, value} = bank;
      return {
        title,
        value,
        source: bank,
      };
    });
  };

  const getSourceBanks = (payload: {sourceBanks: IBank[]}) => {
    return payload.sourceBanks.map(bank => {
      const {title, value} = bank;
      return {
        title,
        value,
        source: bank,
      };
    });
  };

  /**
   * @todo FIX
   */
  const parseRole = (value: string): Role => {
    if (value === Role.client.toString()) {
      return Role.client;
    }
    if (value === Role.subclient.toString()) {
      return Role.subclient;
    }
    if (value === Role.subclientEnchanced.toString()) {
      return Role.subclientEnchanced;
    }
    if (value === Role.salesman.toString()) {
      return Role.salesman;
    }
    return Role.none;
  };

  const getDashboardScreenName = ():
    | 'Dashboard'
    | 'DashboardClient'
    | 'SalesmanDashboard' => {
    if (getRole() === Role.salesman) {
      return 'SalesmanDashboard';
    }
    if (getRole() === Role.client) {
      return 'DashboardClient';
    }
    return 'Dashboard';
  };

  const getProfile = (payload: any): IProfileState | null => {
    if (payload.profile) {
      const profile = payload.profile as IProfile;
      return {
        firstName: profile.firstname,
        lastName: profile.lastname,
        company: profile.company,
        email: profile.email,
        identity: profile.indentity,
        entity: profile.entity,
        role: parseRole(profile.role),
        referenceId: profile.referenceId,
      };
    }
    return null;
  };

  const getHistory = (response: IHistoryResponse) => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getBalanceList = (response: IBalanceListResponse) => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getBalanceSheet = (
    response: IBalanceSheetResponse,
  ): IBalanceSheetItem | null => {
    if (response.payload) {
      return response.payload[0];
    }
    return null;
  };

  const getNotificationList = (
    response: INotificationListResponse,
  ): INotification[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getDebtorList = (response: IDebtorListResponse): IDebtorItem[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getReceivableList = (
    response: IReceivableListResponse,
  ): IReceivable[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getSemaphoreItems = (
    response: ISemaphoreResponse,
  ): ISemaphoreValue[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getClientList = (response: IClientListResponse): IClient[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getUiConfiguration = (payload: any): IUiConfiguration | null => {
    if (payload.uiMobile) {
      const uiConfiguration = payload.uiMobile as IUiConfiguration;
      return uiConfiguration;
    }
    return null;
  };

  const getToken = (): string => {
    return state.session && state.session.token ? state.session.token : '';
  };

  const saveDataSession = (payload: any) => {
    saveSessionValue(payload);
  };

  const clearDataSession = () => {
    sessionValueUtils.removeItem();
  };

  const isOpenedWebSession = (): boolean => {
    if (sessionValue) {
      return true;
    }
    return false;
  };

  const signinWebSession = () => {
    if (sessionValue) {
      const payload = sessionValue;
      dispatchSignIn(payload);
    }
  };

  const dispatchSignIn = (payload: any) => {
    dispatch({
      type: SIGN_IN,
      payload,
    });
    if (isWebApp) {
      saveDataSession(payload);
    }

    if (payload.credentials && payload.credentials.reset) {
      return LoginState.changePassword;
    }

    dispatch({
      type: SET_AUTHENTICATED,
    });

    return LoginState.success;
  };

  const getSalesmanDocumentsHelp = (payload: any) => {
    if (payload.salesman && payload.salesman.requiredDocumentsHelp) {
      return payload.salesman.requiredDocumentsHelp;
    }
    return '';
  };

  /**
   *
   */
  const signin = async (
    email: string,
    password: string,
  ): Promise<LoginState> => {
    try {
      const response = await apiService.postSignIn(
        email.toLowerCase(),
        password,
      );

      if (response.success) {
        const info = getPayloadInfo(response.payload);
        const credentials = getPayloadCredentials(response.payload);
        const banks = getBanks(response.payload);
        const sourceBanks = getSourceBanks(response.payload);
        const profile = getProfile(response.payload);
        const entities = getEntitiesAtLogin(response.payload);
        const phone = getPayloadPhone(response.payload);
        const faq = getPayloadFaq(response.payload);
        const uiConfiguration = getUiConfiguration(response.payload);
        const whatsApp = getPayloadWhatsApp(response.payload);
        const salesTerritories = getSalesmanTerritoriesAtLogin(
          response.payload,
        );
        const customerTypes = getSalesmanCustomerTypesAtLogin(response.payload);
        const customerRequiredDocumentList = getRequiredCustomerDocumentList(
          response.payload,
        );
        const customerRequiredDocumentsHelp = getSalesmanDocumentsHelp(
          response.payload,
        );

        const customerPaymentOptions = getCustomerPaymentOptions(
          response.payload,
        );

        const payload = {
          credentials,
          info,
          banks,
          profile,
          entities,
          phone,
          faq,
          uiConfiguration,
          whatsApp,
          salesTerritories,
          customerTypes,
          customerRequiredDocumentList,
          customerRequiredDocumentsHelp,
          customerPaymentOptions,
          sourceBanks,
        };

        return dispatchSignIn(payload);
      } else if (
        response.error &&
        response.error?.code === `${ErrorCodes.invalid}`
      ) {
        handleLoginCredentialsFail();
        return LoginState.rejected;
      } else if (
        response.error &&
        response.error.code === `${ErrorCodes.crash}`
      ) {
        return LoginState.error;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SIGN_IN);
    }
    return LoginState.error;
  };

  /**
   *
   */
  const signup = async (formData: ISignupForm): Promise<SignUpState> => {
    try {
      const {company, identity, entity, email, firstName, lastName} = formData;
      const response = await apiService.postSignUp({
        company,
        rif: identity,
        entity,
        email,
        firstname: firstName,
        lastname: lastName,
      });

      if (response.success && response.payload) {
        dispatch({
          type: SIGN_UP,
          payload: {
            success: true,
          },
        });
        return SignUpState.success;
      } else {
        return SignUpState.rejected;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SIGN_UP);
    }
    return SignUpState.error;
  };

  /**
   *
   * @returns
   */
  const fetchEntities = async (): Promise<boolean> => {
    try {
      const response: IEntitiesResponse = await apiService.getEntities();

      if (response.success) {
        const entities = getEntities(response);
        dispatch({
          type: ENTITIES,
          payload: {
            entities,
          },
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, ENTITIES);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchBalanceList = async (): Promise<boolean> => {
    try {
      const response: IBalanceListResponse = await apiService.getBalanceList(
        getToken(),
      );

      if (response.success) {
        const balances = getBalanceList(response);

        dispatch({
          type: BALANCE_LIST,
          payload: balances,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, BALANCE_LIST);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchHistoryList = async (): Promise<boolean> => {
    try {
      const response: IHistoryResponse = await apiService.getHistory(
        getToken(),
      );

      if (response.success) {
        const history = getHistory(response);

        dispatch({
          type: HISTORY,
          payload: history,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, HISTORY);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchBalanceSheet = async (): Promise<boolean> => {
    try {
      const response: IBalanceSheetResponse = await apiService.getBalanceSheet(
        getToken(),
      );

      if (response.success) {
        const balance = getBalanceSheet(response);

        dispatch({
          type: BALANCE_SHEET,
          payload: balance,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, BALANCE_LIST);
    }
    return false;
  };

  const fetchNotificationList = async (read?: boolean): Promise<boolean> => {
    try {
      const response: INotificationListResponse =
        await apiService.getNotificationList(getToken(), read);

      if (response.success) {
        const notifications = getNotificationList(response);
        dispatch({
          type: NOTIFICATION_LIST,
          payload: notifications,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, NOTIFICATION_LIST);
    }
    return false;
  };

  const markNotificationsRead = () => {
    dispatch({
      type: MARK_NOTIFICATIONS_READ,
    });
  };

  const addNotification = (title: string, body: string) => {
    dispatch({
      type: ADD_NOTIFICATION,
      payload: {
        title,
        body,
      },
    });
  };

  const setGoToMessages = (value: boolean) => {
    dispatch({
      type: GO_TO_MESSAGES,
      payload: {
        goToMessages: value,
      },
    });
  };

  const fetchSemaphoreItems = async (): Promise<boolean> => {
    try {
      const response: ISemaphoreResponse = await apiService.getSemphoreItems(
        getToken(),
      );

      if (response.success) {
        const semaphoreItems = getSemaphoreItems(response);
        dispatch({
          type: SEMAPHORE_ITEMS,
          payload: semaphoreItems,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEMAPHORE_ITEMS);
    }
    return false;
  };

  const registerDevice = async (
    email: string,
    tokenDevice: string,
    deviceOS: string,
  ) => {
    try {
      const response = await apiService.postRegisterDevice(
        email,
        tokenDevice,
        deviceOS,
        getToken(),
      );

      if (response.success) {
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, REGISTER_DEVICE);
    }
    return false;
  };

  const fetchSalesmanClients = async (): Promise<boolean> => {
    try {
      const response: IClientListResponse = await apiService.getSalesmanClients(
        getToken(),
      );

      if (response.success) {
        const clients = getClientList(response);
        dispatch({
          type: GET_SALESMAN_CLIENTS,
          payload: clients,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEMAPHORE_ITEMS);
    }
    return false;
  };

  const fetchDebtorList = async (key: string): Promise<boolean> => {
    try {
      const response: IDebtorListResponse = await apiService.getDebtorsList(
        key,
        getToken(),
      );

      if (response.success) {
        const documents = getDebtorList(response);
        dispatch({
          type: DEBTOR_LIST,
          payload: documents,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, DEBTOR_LIST);
    }
    return false;
  };

  const fetchReceivableListByDebtor = async (
    key: string,
    rif: string,
  ): Promise<boolean> => {
    try {
      const response: IReceivableListResponse =
        await apiService.getReceivablesByDebtor(getToken(), key, rif);

      if (response.success) {
        const payload = getReceivableList(response);
        dispatch({
          type: RECEIVABLE_LIST,
          payload,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, RECEIVABLE_LIST);
    }
    return false;
  };

  const recoverPassword = async (email: string): Promise<ResultState> => {
    try {
      const response = await apiService.postRecoverPassword({
        email,
      });

      if (response.success) {
        dispatch({
          type: RECOVER_PASSWORD,
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, RECOVER_PASSWORD);
    }
    return ResultState.error;
  };

  const changePassword = async (password: string): Promise<ResultState> => {
    try {
      const response: IStandardServiceResponse =
        await apiService.postChangePassword(
          {
            password,
          },
          getToken(),
        );

      if (response.success) {
        dispatch({
          type: SET_AUTHENTICATED,
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, CHANGE_PASSWORD);
    }
    return ResultState.error;
  };

  const sendEmailBalance = async (
    id: string,
    typeCode: string,
  ): Promise<ResultState> => {
    try {
      const response = await apiService.postSendEmailBalance(
        id,
        typeCode,
        getToken(),
        isSalesMan(),
      );
      const {success} = response;
      if (success) {
        dispatch({
          type: SEND_EMAIL_BALANCE,
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEND_EMAIL_BALANCE);
    }
    return ResultState.error;
  };

  const fetchTyc = async (): Promise<ResultState> => {
    try {
      const response = await apiService.getTyc();
      const tyc = getPayloadTyc(response.payload);
      if (tyc) {
        dispatch({
          type: GET_TYC,
          payload: tyc,
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, GET_TYC);
    }
    return ResultState.error;
  };

  const removeAccount = async (): Promise<ResultState> => {
    try {
      const response = await apiService.deleteAccount(getToken());
      if (response.success) {
        dispatch({
          type: REMOVE_ACCOUNT,
          payload: {},
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, REMOVE_ACCOUNT);
    }
    return ResultState.error;
  };

  const checkIsOutdated = async (): Promise<ResultState> => {
    try {
      const response = await apiService.getAppInfo();
      const isOutdated = verifyIsOutdated(response.payload);
      if (response.success) {
        dispatch({
          type: IS_OUTDATED,
          payload: {
            isOutdated,
          },
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, APP_INFO);
    }
    return ResultState.error;
  };

  const getRole = () => {
    return state.profile?.role;
  };

  const isClient = (): boolean => {
    if (!isAnonymous() && getRole() === Role.client) {
      return true;
    }
    return false;
  };

  const isSubClient = (): boolean => {
    if (isDefaultSubClient() || isEnchancedSubClient()) {
      return true;
    }
    return false;
  };

  const isDefaultSubClient = (): boolean => {
    if (!isAnonymous() && getRole() === Role.subclient) {
      return true;
    }
    return false;
  };

  const isEnchancedSubClient = (): boolean => {
    if (!isAnonymous() && getRole() === Role.subclientEnchanced) {
      return true;
    }
    return false;
  };

  const isSalesMan = (): boolean => {
    if (!isAnonymous() && getRole() === Role.salesman) {
      return true;
    }
    return false;
  };

  const isAnonymous = (): boolean => {
    if (
      state.session &&
      state.session.begin &&
      state.session.token &&
      state.session.lifetime &&
      state.session.signed === true
    ) {
      return false;
    }
    return true;
  };

  /**
   *
   */
  const signOff = () => {
    if (isWebApp) {
      clearDataSession();
    }
    dispatch({
      type: SIGN_OUT,
    });
  };

  /**
   *
   */
  const clearErrors = () => {
    dispatch({
      type: CLEAR_ERRORS,
    });
  };

  const setSalesmanClient = (client: IClient | null) => {
    dispatch({
      type: SET_SALESMAN_CLIENT,
      payload: client,
    });
    return true;
  };

  const setLocalizationActive = (active: boolean) => {
    dispatch({type: SET_LOCALIZATION_ACTIVE, payload: active});
  };

  const loadState = async () => {
    const stateLoaded = await loadFromStorage(USER_STATE_KEY, true);
    if (stateLoaded) {
      dispatch({
        type: LOAD_STATE_FROM_STORE,
        payload: stateLoaded,
      });
    }
  };

  const saveState = async () => {
    saveToStorage(USER_STATE_KEY, state, true);
  };

  const clearStorage = async () => {
    clearFromStorage(USER_STATE_KEY);
  };

  const setRemoteMessageError = (message: string | null) => {
    dispatch({
      type: SET_REMOTE_MESSAGE_ERROR,
      payload: message,
    });
  };

  const value = {
    ...state,
    isAnonymous,
    signin,
    signOff,
    clearErrors,
    fetchEntities,
    fetchBalanceList,
    fetchHistoryList,
    fetchNotificationList,
    signup,
    recoverPassword,
    changePassword,
    sendEmailBalance,
    fetchTyc,
    fetchBalanceSheet,
    removeAccount,
    isClient,
    isSubClient,
    isSalesMan,
    getDashboardScreenName,
    fetchSemaphoreItems,
    fetchDebtorList,
    fetchReceivableListByDebtor,
    checkIsOutdated,
    registerDevice,
    markNotificationsRead,
    addNotification,
    setGoToMessages,
    isOpenedWebSession,
    signinWebSession,
    fetchSalesmanClients,
    setSalesmanClient,
    getToken,
    setLocalizationActive,
    loadState,
    saveState,
    clearStorage,
    isConnected,
    isDefaultSubClient,
    isEnchancedSubClient,
    setRemoteMessageError,
    actions: ACTION_VERBS,
  } as IContext;

  /**
   *
   */
  return <Context.Provider value={value}>{props.children}</Context.Provider>;
};
